import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, ShieldCheck, Clock, Mail, Upload, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { api } from '../App';

/**
 * Payment success page. Payment is confirmed SERVER-SIDE (Stripe) via /api/payments/status.
 * The membership itself activates only after identity verification, so this page shows the live
 * verification state: processing -> verified & active (number shown) | under admin review.
 */
export default function MembershipSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const appId = searchParams.get('app_id');
  const [status, setStatus] = useState('polling'); // polling | paid | expired | timeout | error
  const [application, setApplication] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [verifyPolls, setVerifyPolls] = useState(0);

  // 1) confirm payment with the backend (never trusts the redirect)
  useEffect(() => {
    if (!sessionId) {
      // No Stripe session (e.g. link opened later): accept only an application the server already shows as activated
      if (appId) {
        api.get(`/api/membership/status/${appId}`).then(d => {
          if (d.application?.activation_state === 'activated') { setApplication(d.application); setStatus('paid'); } else setStatus('error');
        }).catch(() => setStatus('error'));
      } else setStatus('error');
      return;
    }
    if (status !== 'polling') return;
    const poll = async () => {
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`);
        if (res.payment_status === 'paid') { setStatus('paid'); return; }
        if (res.status === 'expired') { setStatus('expired'); return; }
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000); else setStatus('timeout');
      } catch {
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000); else setStatus('error');
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attempts]);

  // 2) once paid, follow the verification pipeline
  useEffect(() => {
    if (status !== 'paid' || !appId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const d = await api.get(`/api/membership/status/${appId}${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`);
        if (cancelled) return;
        setApplication(d.application);
        const vs = d.application?.verification_status;
        const done = d.application?.activation_state === 'activated' || ['admin_review', 'rejected', 'document_required', 'on_hold'].includes(vs) || d.application?.status === 'admin_review';
        if (!done && verifyPolls < 40) setTimeout(() => setVerifyPolls(p => p + 1), 3000);
      } catch { if (verifyPolls < 40) setTimeout(() => setVerifyPolls(p => p + 1), 3000); }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, appId, verifyPolls]);

  if (status === 'polling') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="membership-success-polling">
        <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--gs-saffron))] mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Confirming your payment...</h2>
        <p className="text-[hsl(var(--muted-foreground))]">Please wait while we confirm your payment securely with Stripe.</p>
      </div>
    );
  }

  if (status !== 'paid') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="membership-success-failed">
        <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>We could not confirm the payment yet</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">If money has left your account, please do not pay again — your membership will be processed automatically once Stripe confirms it, and you will receive an email. Otherwise you can try again or contact us.</p>
        <div className="flex gap-3 justify-center"><Link to="/membership"><Button variant="outline">Try Again</Button></Link><Link to="/contact"><Button>Contact Us</Button></Link></div>
      </div>
    );
  }

  const activated = application?.activation_state === 'activated';
  const vs = application?.verification_status;
  const review = !activated && (vs === 'admin_review' || application?.status === 'admin_review' || vs === 'rejected');
  const documentRequired = !activated && !review && (vs === 'document_required' || vs === 'on_hold') && !application?.has_document;
  const processing = !activated && !review && !documentRequired;
  const typeLabel = application?.membership_type === 'lifetime' ? 'Lifetime Member' : application?.membership_type === 'senior_65plus' ? 'Age 65+ Lifetime Member' : 'Standard Membership (2 years)';
  const dueDate = application?.document_due_date ? new Date(application.document_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="membership-success-page">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>{application?.renewal_of ? 'Thank you for renewing!' : 'Payment received — thank you!'}</h1>
      <p className="text-[hsl(var(--muted-foreground))] mb-6" data-testid="membership-success-payment-msg">{application?.renewal_of ? 'Your payment has been confirmed and your membership has been extended. Your membership number and card stay the same.' : 'Thank you for joining us — your payment has been confirmed.'}</p>
      {application?.renewal_of && application?.renewal_result && (
        <Card className="text-left max-w-md mx-auto mb-4" data-testid="membership-success-renewal">
          <CardContent className="p-6 space-y-2">
            <div><span className="text-sm text-[hsl(var(--muted-foreground))]">Membership Number</span><div className="font-bold text-lg" style={{ fontFamily: 'Space Grotesk' }}>{application.renewal_of}</div></div>
            <div className="text-sm">{application.renewal_result.type_label} · {application.renewal_result.new_expiry ? <>valid until <span className="font-medium">{application.renewal_result.new_expiry}</span></> : 'lifetime'}</div>
            {application.renewal_result.children_aged_out?.length > 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">{application.renewal_result.children_aged_out.map(c => c.full_name).join(', ')} {application.renewal_result.children_aged_out.length === 1 ? 'is' : 'are'} now 14 or over and would be welcome to join as a member in their own right.</p>}
          </CardContent>
        </Card>
      )}

      {!application?.renewal_of && <Card className="text-left max-w-md mx-auto">
        <CardContent className="p-6 space-y-3">
          {application?.membership_number && !activated && (
            <div className="pb-3 border-b" data-testid="membership-success-provisional">
              <span className="text-sm text-[hsl(var(--muted-foreground))]">Your Membership Number</span>
              <div className="font-bold text-lg text-[hsl(var(--gs-saffron))]" style={{ fontFamily: 'Space Grotesk' }} data-testid="membership-success-number">{application.membership_number}</div>
              <div className="text-sm">{typeLabel} · <span className="font-medium text-amber-700" data-testid="membership-success-status">Provisional — identity verification pending</span></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Your number and payment are secured. Member benefits and your digital card unlock once your ID is verified.</p>
            </div>
          )}
          {documentRequired && (
            <div className="flex items-start gap-3" data-testid="membership-success-document-required">
              <Upload className="w-5 h-5 text-[hsl(var(--gs-saffron))] mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Upload your ID document{dueDate ? ` by ${dueDate}` : ''}</div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">A passport, driving licence or similar showing your full name and date of birth. You can also use the secure link in your confirmation email.</p>
                {application?.upload_url && <a href={application.upload_url} data-testid="membership-success-upload-link"><Button className="mt-3 bg-[hsl(var(--gs-navy))] text-white"><Upload className="w-4 h-4 mr-2" /> Upload Document</Button></a>}
              </div>
            </div>
          )}
          {processing && (
            <div className="flex items-start gap-3" data-testid="membership-success-verifying">
              <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--gs-saffron))] mt-0.5" />
              <div>
                <div className="font-medium flex items-center gap-1"><FileCheck className="w-4 h-4" /> Document received — verifying your identity…</div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">We are checking the name and date of birth on your ID against your application. This usually takes under a minute.</p>
              </div>
            </div>
          )}
          {activated && (
            <>
              <div className="flex items-center gap-2 text-green-700 font-medium" data-testid="membership-success-verified"><ShieldCheck className="w-5 h-5" /> Identity verified — membership active</div>
              <div><span className="text-sm text-[hsl(var(--muted-foreground))]">Name</span><div className="font-medium">{application.full_name}</div></div>
              <div>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Membership Number</span>
                <div className="font-bold text-lg text-[hsl(var(--gs-saffron))]" style={{ fontFamily: 'Space Grotesk' }} data-testid="membership-success-number">{application.membership_number}</div>
              </div>
              <div>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Type</span>
                <div className="font-medium">{application.membership_type === 'lifetime' ? 'Lifetime Member' : application.membership_type === 'senior_65plus' ? '65+ Lifetime Member' : 'Standard Membership (2 years)'}</div>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /> Your digital membership card with QR code is on its way to {application.email}. Keep your membership number safe — you will need it for member-priced tickets.</p>
            </>
          )}
          {review && (
            <div className="flex items-start gap-3" data-testid="membership-success-review">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="font-medium">Your application is with our team</div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">We could not automatically match your ID document to your application, so an administrator will review it personally. Your payment is safe. You will receive your membership number and digital card by email once approved — usually within a few days.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>}

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/"><Button variant="outline">Back to Home</Button></Link>
        <Link to="/events"><Button className="bg-[hsl(var(--primary))] text-white">Browse Events</Button></Link>
      </div>
    </div>
  );
}
